import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Activity, Compass, Handshake, Megaphone, IdCard, FileText, Mic, FolderOpen,
  MessageSquare, DollarSign, ShieldCheck, Settings, Wallet, TrendingUp, TrendingDown,
  Clock, CheckCircle2, Loader2, Receipt, Info, Download, Landmark,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { UpgradePromptBanner } from '../components/brand/TierComponents';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { money } from '../lib/rcm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SETTLEMENT_META = {
  held: { label: 'Awaiting verification', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  pending: { label: 'Awaiting verification', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  released: { label: 'Settled', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  refunded: { label: 'Refunded', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const Kpi = ({ icon: Icon, label, value, sub, accent = 'pine', testId, badge }) => {
  const a = { pine: 'bg-cn-primary/10 text-cn-primary', terracotta: 'bg-cn-terracotta-50 text-cn-terracotta-600', amber: 'bg-amber-50 text-amber-600', sage: 'bg-cn-sage-100 text-cn-primary' }[accent];
  return (
    <div className="cn-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <span className={`w-8 h-8 rounded-xl flex items-center justify-center ${a}`}><Icon className="w-4 h-4" /></span>
      </div>
      <div className="mt-2 font-display font-bold text-2xl text-cn-text dark:text-white flex items-center gap-2">
        {value}{badge}
      </div>
      {sub && <div className="text-xs text-cn-text-muted mt-0.5">{sub}</div>}
    </div>
  );
};

const MONTH_LABEL = (mk) => {
  const [, m] = mk.split('-');
  return ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parseInt(m, 10)] || mk;
};

export default function ClinicianPayouts() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [stmtYear, setStmtYear] = useState('all');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clinician/payouts`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      toast.error('Could not load earnings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const url = stmtYear === 'all'
        ? `${API}/clinician/payouts/statement`
        : `${API}/clinician/payouts/statement?year=${stmtYear}`;
      const res = await axios.get(url, { ...getAuthHeader(), responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `caraloom-earnings-${stmtYear === 'all' ? new Date().toISOString().slice(0, 10) : stmtYear}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Statement downloaded');
    } catch (e) {
      toast.error('Could not download statement');
    } finally {
      setDownloading(false);
    }
  };

  // Tax years available for the statement: current year + any years in the ledger.
  const statementYears = Array.from(new Set([
    new Date().getFullYear(),
    ...(data?.transactions || []).map((t) => t.date && parseInt(t.date.slice(0, 4), 10)).filter(Boolean),
  ])).sort((a, b) => b - a);

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/clinician/dashboard' },
    { icon: Compass, label: 'Practice Coach', href: '/clinician/coach', highlight: true },
    { icon: Handshake, label: 'Offers Inbox', href: '/clinician/offers' },
    { icon: Megaphone, label: 'Care Requests', href: '/clinician/care-requests' },
    { icon: IdCard, label: 'Practice Profile', href: '/clinician/dashboard#profile' },
    { icon: FileText, label: 'Clinical Notes', href: '/clinician/notes' },
    { icon: Mic, label: 'Voice Notes', href: '/clinician/voice-notes' },
    { icon: FolderOpen, label: 'Documents', href: '/clinician/documents' },
    { icon: MessageSquare, label: 'Messages', href: '/clinician/messages' },
    { icon: Wallet, label: 'Earnings & Payouts', active: true },
    { icon: Landmark, label: 'Banking & Benefits', href: '/clinician/banking' },
    { icon: DollarSign, label: 'Billing & RCM', href: '/clinician/billing' },
    { icon: ShieldCheck, label: 'Privacy & Data', href: '/privacy/dashboard' },
    { icon: Settings, label: 'Settings', href: '#' },
  ];

  const s = data?.summary || {};
  const monthly = data?.monthly || [];
  const maxNet = Math.max(1, ...monthly.map((m) => m.net));
  const wow = data?.wow_change_pct;
  const wowBadge = wow === null || wow === undefined ? null : (
    <span className={`text-xs font-bold inline-flex items-center gap-0.5 ${wow >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="payouts-wow">
      {wow >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
      {Math.abs(wow)}%
    </span>
  );

  return (
    <DashboardShell
      navItems={navItems}
      title="Earnings & payouts"
      subtitle="Every dollar you've earned on CaraLoom — week over week, what's awaiting service verification, and what's been settled. Net of our transparent service rate."
      testId="clinician-payouts-page"
      rightSlot={
        <div className="flex items-center gap-2">
          <Select value={stmtYear} onValueChange={setStmtYear}>
            <SelectTrigger className="w-[130px]" data-testid="statement-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              {statementYears.map((y) => (
                <SelectItem key={y} value={String(y)}>{y} (1099)</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={downloadStatement}
            disabled={downloading || !(data?.transactions || []).length}
            data-testid="download-statement-btn"
            className="cn-btn cn-btn-primary !py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download statement
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="grid sm:grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="cn-card h-28 animate-pulse bg-cn-bg/60" />)}</div>
      ) : (
        <div className="space-y-6" data-testid="payouts-content">
          <UpgradePromptBanner monthlyBillings={s.this_month_net || 0} />

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="payouts-kpis">
            <Kpi icon={TrendingUp} label="This week" value={money(data?.this_week_net)} sub={`vs ${money(data?.last_week_net)} last week`} accent="sage" testId="kpi-this-week" badge={wowBadge} />
            <Kpi icon={Receipt} label="This month" value={money(s.this_month_net)} sub={`${s.lifetime_visits || 0} paid visit(s) all-time`} accent="pine" testId="kpi-this-month" />
            <Kpi icon={Clock} label="Awaiting verification" value={money(s.pending_escrow)} sub="Settles when visit is documented" accent="amber" testId="kpi-awaiting-verification" />
            <Kpi icon={CheckCircle2} label="Settled" value={money(s.released_total)} sub="Available / paid out" accent="sage" testId="kpi-settled" />
          </div>

          {/* Lifetime + trend */}
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="cn-card p-5 lg:col-span-1" data-testid="payouts-lifetime">
              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Lifetime</p>
              <div className="mt-3 space-y-2.5 text-sm">
                <div className="flex justify-between"><span className="text-cn-text-muted">Gross billed</span><span className="font-semibold text-cn-text dark:text-white">{money(s.all_time_gross)}</span></div>
                <div className="flex justify-between"><span className="text-cn-text-muted">Service rate ({Math.round((s.platform_fee_pct || 0.12) * 100)}%)</span><span className="font-semibold text-red-600">−{money(s.platform_fees)}</span></div>
                <div className="flex justify-between border-t border-cn-border pt-2.5"><span className="text-cn-text-muted">Your net earnings</span><span className="font-display font-bold text-lg text-cn-primary">{money(s.all_time_net)}</span></div>
              </div>
            </div>

            <div className="cn-card p-5 lg:col-span-2" data-testid="payouts-trend">
              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-4">Net earnings · last 6 months</p>
              <div className="flex items-stretch justify-between gap-3 h-40 pt-6">
                {monthly.map((m) => (
                  <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-2" data-testid={`trend-${m.month}`}>
                    <div className="w-full flex-1 flex items-end justify-center">
                      <div
                        className="w-full max-w-[44px] rounded-t-lg bg-cn-primary/80 transition-all relative"
                        style={{ height: `${Math.max(2, (m.net / maxNet) * 100)}%` }}
                      >
                        {m.net > 0 && (
                          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-cn-text-muted whitespace-nowrap">{money(m.net)}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-cn-text-muted">{MONTH_LABEL(m.month)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Verified-settlement / payout explainer */}
          <div className="cn-card p-4 flex items-start gap-3 bg-cn-sage-50/60 border-cn-sage-200" data-testid="payouts-settlement-note">
            <Info className="w-5 h-5 text-cn-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-cn-text-muted">
              Funds for each visit are secured up-front and automatically settle to you <strong className="text-cn-text dark:text-white">via Service Verification</strong> the moment your SOAP note is signed.
              Settled funds are paid out to your connected bank account through Stripe Connect.
            </p>
          </div>

          {/* Transactions ledger */}
          <div className="cn-card overflow-hidden" data-testid="payouts-ledger">
            <div className="px-5 py-4 border-b border-cn-border">
              <h3 className="font-display font-bold text-cn-text dark:text-white">Payment history</h3>
              <p className="text-xs text-cn-text-muted">Every paid visit, with the platform fee and your net.</p>
            </div>
            {(data?.transactions || []).length === 0 ? (
              <div className="px-5 py-12 text-center" data-testid="payouts-empty">
                <Wallet className="w-10 h-10 text-cn-text-muted/40 mx-auto mb-3" />
                <p className="text-cn-text-muted">No payments yet. Earnings appear here once a booked visit is paid.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-cn-text-muted border-b border-cn-border">
                      <th className="px-5 py-3 font-bold">Date</th>
                      <th className="px-5 py-3 font-bold">Patient</th>
                      <th className="px-5 py-3 font-bold">Service</th>
                      <th className="px-5 py-3 font-bold text-right">Gross</th>
                      <th className="px-5 py-3 font-bold text-right">Fee</th>
                      <th className="px-5 py-3 font-bold text-right">Net</th>
                      <th className="px-5 py-3 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((t, i) => {
                      const m = SETTLEMENT_META[t.escrow_status] || SETTLEMENT_META.pending;
                      return (
                        <tr key={t.id || i} className="border-b border-cn-border/60 last:border-0" data-testid={`payout-row-${i}`}>
                          <td className="px-5 py-3 text-cn-text-muted whitespace-nowrap">{t.date}</td>
                          <td className="px-5 py-3 font-medium text-cn-text dark:text-white">{t.patient_name}</td>
                          <td className="px-5 py-3 text-cn-text-muted">{t.service_type}</td>
                          <td className="px-5 py-3 text-right text-cn-text dark:text-white">{money(t.gross)}</td>
                          <td className="px-5 py-3 text-right text-red-600">−{money(t.platform_fee)}</td>
                          <td className="px-5 py-3 text-right font-semibold text-cn-primary">{money(t.net)}</td>
                          <td className="px-5 py-3"><span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
